import React, { useContext, useRef, useState } from 'react';
import { ListGroup, ListGroupItem } from 'reactstrap';
import PerfectScrollbar from 'react-perfect-scrollbar';
import useTranslation from '@hooks/useTranslation';
import widgetList from '../index';
import { editWidget } from '../../MainPages/Settings/ScreenManagement/ScreenEdit/reducer';
import { TextInput } from '@components';
import { ScreenEditDispatchContext } from '../../MainPages/Settings/ScreenManagement/ScreenEdit';
import Modal from '@components/Modal';
import Button from '@components/Button';
import cx from 'classnames';

const CATEGORY_ALL = 'All';

const WidgetList = ({ widgetList, activeTarget, onClick }) => {
    const t = useTranslation('WidgetList');
    // 각 카테고리 count
    const categoryCount = {};
    Object.values(widgetList).forEach(({ category }) => {
        if (!category) {
            return;
        }
        if (!categoryCount[category]) {
            categoryCount[category] = 1;
            return;
        }
        categoryCount[category] += 1;
    });
    // 전체 count
    const totalCount = Object.values(categoryCount).reduce((a, b) => a + b, 0);

    const isAll = activeTarget === CATEGORY_ALL;
    return (
        <>
            <ListGroupItem
                key="widgetList_total"
                tag="button"
                onClick={onClick}
                value={CATEGORY_ALL}
                className={`layout`}
            >
                <div className={'widgetCounts'}>
                    <div>
                        <span className={cx(isAll && 'color-brand', 'title')}>{t('Total')}</span>
                        <span
                            className={cx(isAll ? 'color-brand' : 'color-gray', 'subTitle')}
                        >{`(${totalCount})`}</span>
                    </div>
                    <div className={cx(isAll ? 'icon-filter-brand' : 'icon-filter-black', 'icon-next')} />
                </div>
            </ListGroupItem>
            {Object.keys(categoryCount).map((key, index) => (
                <ListGroupItem key={`${key}_${index}`} tag="button" onClick={onClick} value={key}>
                    <div className={'widgetCounts'}>
                        <div>
                            <span className={cx(activeTarget === key && 'color-brand', 'title')}>{t(key)}</span>
                            <span
                                className={cx(activeTarget === key ? 'color-brand' : 'color-gray', 'subTitle')}
                            >{`(${categoryCount[key]})`}</span>
                        </div>
                        <div
                            className={cx(
                                activeTarget === key ? 'icon-filter-brand' : 'icon-filter-black',
                                'icon-next',
                            )}
                        />
                    </div>
                </ListGroupItem>
            ))}
        </>
    );
};

const WidgetPrevList = ({ widgetList, layout, toggleModal }) => {
    const t = useTranslation('WidgetList');
    const dispatch = useContext(ScreenEditDispatchContext);
    const handleAddClick = (e, widgetInfo) => {
        dispatch(
            editWidget({
                name: widgetInfo.name,
                category: widgetInfo.category,
                id: layout.i,
                type: widgetInfo.type,
                config: widgetInfo.config,
                layout: { ...layout, ...widgetInfo.layout },
            }),
        );
        toggleModal();
    };

    // template widget 제거
    const widgets = Object.values(widgetList).filter(({ type }) => type !== 'temp');
    // widget 없는 경우
    if (!widgets.length) {
        return (
            <ListGroupItem key={'noItem'} className="d-flex flex-center">
                <p className="mb-0">{t('There are no search widget')}</p>
            </ListGroupItem>
        );
    }

    return (
        <>
            {widgets.map((widget, index) => (
                <div key={`${widget.name}_${index}`} className="widgetItemWrapper">
                    <div className={'flx-row justify-content-center'}>
                        <div>
                            <span className={widget.thumbnailClassName} />
                        </div>
                        <div className="w-100 p-3">
                            <span className="widgetName">{t(widget.name, 'Widget')}</span>
                            <p className="widgetDesc">{t(widget.desc, 'Widget')}</p>
                        </div>
                        <Button
                            className="btn-brand btn-icon btn-icon-only"
                            iconName="add"
                            onClick={e => {
                                handleAddClick(e, widget);
                            }}
                        />
                    </div>
                </div>
            ))}
        </>
    );
};

const PalettePopup = ({
    initOpen = false,
    setModal,
    toggleModal = function () {
        initOpen = !initOpen;
    },
    layout,
    ...restProps
}) => {
    const t = useTranslation('Widget');
    const [filteredList, setFilteredList] = useState(widgetList);
    const [currentCategory, setCurrentCategory] = useState(CATEGORY_ALL);
    const ps = useRef();

    const scrollTop = () => {
        const curr = ps.current;
        if (curr) {
            curr.scrollTop = 0;
        }
    };

    // 검색어 변경 이벤트
    const handleFilterChange = e => {
        const value = e.currentTarget.value;
        const filterValue = Object.fromEntries(
            Object.entries(widgetList).filter(
                ([_, { name }]) => t(name).toUpperCase().indexOf(value.toUpperCase()) >= 0,
            ),
        );
        setCurrentCategory(CATEGORY_ALL);
        setFilteredList(filterValue);
    };

    // 카테고리 선택 이벤트
    const onListClick = e => {
        const value = e.currentTarget.value;
        scrollTop();

        // 전체 선택
        if (value === CATEGORY_ALL) {
            setCurrentCategory(CATEGORY_ALL);
            setFilteredList(widgetList);
            return;
        }

        // 카테고리 선택
        const filterValue = Object.fromEntries(
            Object.entries(widgetList).filter(([_, { category }]) => category && category === value),
        );
        setCurrentCategory(value);
        setFilteredList(filterValue);
    };

    return (
        <Modal
            className={'widget-add-popup'}
            initModal={initOpen}
            toggleModal={toggleModal}
            style={{ maxWidth: '45rem' }}
            // headerTitle={t('Add Widget', 'WidgetList')}
            removeCancel
            {...restProps}
            modalHeader={
                <div className={'modal-header w-100'}>
                    <div className={'flx-row justify-content-between w-100 align-center'}>
                        <span className={'s-8 font-weight-bold'}>{t('Add Widget', 'WidgetList')}</span>
                        <span className={'icon-close cursor-pointer'} onClick={toggleModal} />
                    </div>
                </div>
            }
        >
            <div className="modal-body d-flex p-0">
                <div className="w-40 py-3">
                    <TextInput
                        type={'text'}
                        name={'dashboard-palette-popup'}
                        placeholder={t('Search', 'Search')}
                        handleChange={handleFilterChange}
                        inputGroupClassName={'w-100 px-3 mb-3'}
                    />
                    <ListGroup flush className="w-100">
                        <WidgetList widgetList={widgetList} activeTarget={currentCategory} onClick={onListClick} />
                    </ListGroup>
                </div>

                <div className="w-60" style={{ height: '400px' }}>
                    <PerfectScrollbar containerRef={e => (ps.current = e)}>
                        <ListGroup flush>
                            <WidgetPrevList layout={layout} toggleModal={toggleModal} widgetList={filteredList} />
                        </ListGroup>
                    </PerfectScrollbar>
                </div>
            </div>
        </Modal>
    );
};

export default PalettePopup;
